import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Star, Check, X, ArrowCounterClockwise, Copy } from "@phosphor-icons/react";

const CHIP = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-stone-200 text-stone-500",
};
const ASK_CHIP = {
  pending: "bg-amber-100 text-amber-800",
  "left-review": "bg-emerald-100 text-emerald-800",
  declined: "bg-stone-200 text-stone-500",
};

const smsText = (name, link) =>
  `Hi ${(name || "").split(" ")[0]}, this is the office at Silver Home Care — thank you for being with us. If you have a minute, a Google review helps other Bucks County families find us: ${link} — no pressure either way, and thank you!`;

export default function ReviewsAdminPage() {
  const [reviews, setReviews] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [asks, setAsks] = useState(null);
  const [checkins, setCheckins] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    api.get("/reviews").then((r) => setReviews(r.data || [])).catch((e) => {
      toast.error(formatApiError(e));
      setReviews([]);
    });
    api.get("/review-asks/candidates").then((r) => setCandidates(r.data)).catch(() => setCandidates({ clients: [], caregivers: [], review_link: "" }));
    api.get("/review-asks").then((r) => setAsks(r.data || [])).catch(() => setAsks([]));
    api.get("/review-asks/check-ins").then((r) => setCheckins(r.data || [])).catch(() => setCheckins([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/reviews/${id}/status`, { status });
      toast.success(`Review ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markAsked = async (subjectType, subjectId, name) => {
    try {
      await api.post("/review-asks", { subject_type: subjectType, subject_id: subjectId, name });
      await navigator.clipboard.writeText(smsText(name, candidates?.review_link || ""));
      toast.success(`Ask recorded — text for ${name.split(" ")[0]} copied to your clipboard`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setOutcome = async (id, outcome) => {
    try {
      await api.post(`/review-asks/${id}/outcome`, { outcome });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const lastAskedBy = useMemo(() => {
    const m = {};
    (asks || []).forEach((a) => {
      const k = `${a.subject_type}:${a.subject_id}`;
      if (!m[k]) m[k] = a;
    });
    return m;
  }, [asks]);

  const allPeople = useMemo(() => {
    if (!candidates) return [];
    const q = search.trim().toLowerCase();
    const list = [
      ...(candidates.clients || []).map((c) => ({ ...c, type: "client" })),
      ...(candidates.caregivers || []).map((c) => ({ ...c, type: "caregiver" })),
    ].filter((p) => p.name);
    if (!q) return list.slice(0, 15);
    return list.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 25);
  }, [candidates, search]);

  const pendingCount = (reviews || []).filter((r) => r.status === "pending").length;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8" data-testid="admin-reviews-page">
      <h1 className="font-heading text-2xl font-semibold text-[#23282f]">Reviews & testimonials</h1>
      <p className="mt-2 text-sm text-stone-600">
        Approve a review to publish it on the public reviews page. Rejected reviews stay here but never appear on the site.
        {pendingCount > 0 && <span className="ml-2 font-semibold text-amber-700">{pendingCount} waiting for you.</span>}
      </p>

      <div className="mt-6 space-y-4" data-testid="admin-reviews-list">
        {reviews === null ? null : reviews.length === 0 ? (
          <p className="rounded-xl border border-stone-200 bg-white p-8 text-stone-500" data-testid="admin-reviews-empty">
            No reviews yet. The public page is at /reviews.
          </p>
        ) : (
          reviews.map((r) => (
            <div key={r.id} className={`rounded-xl border bg-white p-5 ${r.status === "rejected" ? "border-stone-200 opacity-60" : "border-stone-200"}`} data-testid={`review-row-${r.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} size={15} weight={i <= r.rating ? "fill" : "regular"}
                      className={i <= r.rating ? "text-[#6A3E97]" : "text-stone-300"} />
                  ))}
                </span>
                <span className="font-semibold text-[#23282f]">{r.name}</span>
                {r.relationship && <span className="text-sm text-stone-500">· {r.relationship}</span>}
                <span className={`ml-auto rounded-full px-2.5 py-0.5 text-xs font-semibold ${CHIP[r.status] || CHIP.pending}`} data-testid={`review-status-${r.id}`}>
                  {r.status}
                </span>
              </div>
              <p className="mt-3 leading-relaxed text-stone-700">"{r.text}"</p>
              <p className="mt-1 text-xs text-stone-400">{new Date(r.created_at).toLocaleString()}</p>
              <div className="mt-4 flex gap-2">
                {r.status !== "approved" && (
                  <Button size="sm" onClick={() => setStatus(r.id, "approved")}
                    className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`approve-${r.id}`}>
                    <Check size={14} className="mr-1" /> Approve & publish
                  </Button>
                )}
                {r.status !== "rejected" && (
                  <Button size="sm" variant="outline" onClick={() => setStatus(r.id, "rejected")}
                    className="border-stone-300 text-stone-600" data-testid={`reject-${r.id}`}>
                    <X size={14} className="mr-1" /> Reject
                  </Button>
                )}
                {r.status !== "pending" && (
                  <Button size="sm" variant="ghost" onClick={() => setStatus(r.id, "pending")}
                    className="text-stone-500" data-testid={`unpublish-${r.id}`}>
                    <ArrowCounterClockwise size={14} className="mr-1" /> Back to pending
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-12 border-t border-stone-200 pt-8" data-testid="review-asks">
        <h2 className="font-heading text-xl font-semibold text-[#23282f]">Ask for Google reviews</h2>
        <p className="mt-2 text-sm leading-relaxed text-stone-600">
          Ask at the warm moment — a compliment call, a good check-in, a resolved problem. Clicking{" "}
          <span className="font-semibold">Mark asked</span> records it here and copies a ready-to-send text
          with your Google review link. This page never sends anything itself — you send the text from your own phone.
        </p>

        {checkins !== null && (
          <div className="mt-5 rounded-xl border border-[#6A3E97]/25 bg-[#6A3E97]/5 p-5" data-testid="checkins-card">
            <h3 className="font-heading text-base font-semibold text-[#23282f]">30-day check-ins</h3>
            <p className="mt-1 text-sm text-stone-600">
              Clients hitting 30 days since their care started — the natural moment to check in and, if they're happy, ask.
            </p>
            {checkins.length === 0 ? (
              <p className="mt-3 text-sm text-stone-500" data-testid="checkins-empty">Nobody hits their 30-day mark right now.</p>
            ) : (
              <div className="mt-3 space-y-2">
                {checkins.map((c) => (
                  <div key={c.id} className="flex flex-wrap items-center gap-3 rounded-lg bg-white px-4 py-2.5" data-testid={`checkin-${c.id}`}>
                    <span className="font-medium text-[#23282f]">{c.name}</span>
                    <span className="text-xs text-stone-500">
                      started {new Date(c.service_start_date).toLocaleDateString()} · {c.days_since_start} days ago
                    </span>
                    <Button size="sm" onClick={() => markAsked("client", c.id, c.name)}
                      className="ml-auto bg-[#6A3E97] text-white hover:bg-[#55317A]" data-testid={`checkin-ask-${c.id}`}>
                      <Copy size={13} className="mr-1" /> Mark asked & copy text
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search active clients and caregivers…" className="mt-4 h-11 max-w-md" data-testid="review-ask-search" /><div className="mt-4 divide-y divide-stone-100 rounded-xl border border-stone-200 bg-white" data-testid="ask-candidates">
          {allPeople.map((p) => {
            const last = lastAskedBy[`${p.type}:${p.id}`];
            return (
              <div key={`${p.type}-${p.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`ask-row-${p.type}-${p.id}`}>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${p.type === "client" ? "bg-[#6A3E97]/10 text-[#6A3E97]" : "bg-stone-100 text-stone-600"}`}>
                  {p.type === "client" ? "Client" : "Caregiver"}
                </span>
                <span className="font-medium text-[#23282f]">{p.name}</span>
                {last && (
                  <span className="text-xs text-stone-400">
                    asked {new Date(last.asked_at).toLocaleDateString()}
                  </span>
                )}
                <Button size="sm" onClick={() => markAsked(p.type, p.id, p.name)}
                  className="ml-auto bg-[#6A3E97] text-white hover:bg-[#55317A]" data-testid={`mark-asked-${p.type}-${p.id}`}>
                  <Copy size={13} className="mr-1" /> Mark asked & copy text
                </Button>
              </div>
            );
          })}
          {candidates && allPeople.length === 0 && (
            <p className="px-4 py-6 text-sm text-stone-500" data-testid="ask-no-results">No matches — check the spelling.</p>
          )}
        </div>
        {!search && <p className="mt-1.5 text-xs text-stone-400">Showing the first 15 — search to find anyone else.</p>}

        <h3 className="mt-8 font-heading text-lg font-semibold text-[#23282f]">Ask history</h3>
        <div className="mt-3 space-y-3" data-testid="ask-history">
          {(asks || []).length === 0 ? (
            <p className="rounded-xl border border-stone-200 bg-white p-6 text-sm text-stone-500" data-testid="ask-history-empty">
              Nobody asked yet. When you ask someone, it shows up here so nobody gets asked twice.
            </p>
          ) : (
            asks.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3" data-testid={`ask-history-${a.id}`}>
                <span className="font-medium text-[#23282f]">{a.name}</span>
                <span className="text-xs text-stone-400">
                  {a.subject_type === "client" ? "Client" : "Caregiver"} · asked {new Date(a.asked_at).toLocaleDateString()} by {a.asked_by}
                </span>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${ASK_CHIP[a.outcome] || ASK_CHIP.pending}`}>
                  {a.outcome === "left-review" ? "Left a review" : a.outcome}
                </span>
                <div className="ml-auto flex gap-2">
                  {a.outcome !== "left-review" && (
                    <Button size="sm" variant="outline" onClick={() => setOutcome(a.id, "left-review")}
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`askoutcome-left-${a.id}`}>
                      Left a review
                    </Button>
                  )}
                  {a.outcome !== "declined" && (
                    <Button size="sm" variant="ghost" onClick={() => setOutcome(a.id, "declined")}
                      className="text-stone-500" data-testid={`askoutcome-declined-${a.id}`}>
                      Declined
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
